import { useState, useEffect, useMemo, useCallback } from "react";

const API_BASE = "https://prices.runescape.wiki/api/v1/osrs";
const WIKI_ICON_BASE = "https://oldschool.runescape.wiki/images";
const USER_AGENT = "ge-flip-tracker - OSRS Flip Tool";

// GE Fee: 1% of sell price, rounded down, capped at 5M
function geFee(sellPrice) {
  return Math.min(Math.floor(sellPrice * 0.01), 5000000);
}

function formatGp(num) {
  if (num === null || num === undefined) return "—";
  if (Math.abs(num) >= 1000000000) return (num / 1000000000).toFixed(1) + "B";
  if (Math.abs(num) >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (Math.abs(num) >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toLocaleString();
}

function formatTime(unix) {
  if (!unix) return "—";
  const diff = Math.floor(Date.now() / 1000) - unix;
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function roiColor(roi) {
  if (roi >= 5) return "#22c55e";
  if (roi >= 2) return "#84cc16";
  if (roi >= 0) return "#eab308";
  return "#ef4444";
}

const SORT_OPTIONS = [
  { key: "margin", label: "Margin" },
  { key: "roi", label: "ROI %" },
  { key: "marginXVolume", label: "Margin × Vol" },
  { key: "volume", label: "Volume" },
  { key: "high", label: "Buy Price" },
];

const FILTER_PRESETS = [
  { label: "All", minPrice: 0, maxPrice: Infinity, minVolume: 0 },
  { label: "Budget (<100K)", minPrice: 1, maxPrice: 100000, minVolume: 50 },
  { label: "Mid (100K–5M)", minPrice: 100000, maxPrice: 5000000, minVolume: 20 },
  { label: "High (5M+)", minPrice: 5000000, maxPrice: Infinity, minVolume: 5 },
  { label: "High Volume", minPrice: 0, maxPrice: Infinity, minVolume: 500 },
];

export default function GEFlipTracker() {
  const [items, setItems] = useState([]);
  const [prices, setPrices] = useState({});
  const [avgPrices, setAvgPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("marginXVolume");
  const [sortDir, setSortDir] = useState("desc");
  const [filterPreset, setFilterPreset] = useState(0);
  const [search, setSearch] = useState("");
  const [membersOnly, setMembersOnly] = useState(false);
  const [pinned, setPinned] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [tsLoading, setTsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      
      const headers = { "User-Agent": USER_AGENT };
      
      const [mappingRes, latestRes, avgRes] = await Promise.all([
        fetch(`${API_BASE}/mapping`),
        fetch(`${API_BASE}/latest`),
        fetch(`${API_BASE}/5m`),
      ]);
      
      const mapping = await mappingRes.json();
      const latest = await latestRes.json();
      const avg = await avgRes.json();
      
      setItems(mapping);
      setPrices(latest.data || {});
      setAvgPrices(avg.data || {});
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError("Failed to fetch GE data. The Wiki API might be temporarily unavailable.");
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 120000); // refresh every 2 mins
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchTimeseries = async (itemId) => {
    setTsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/timeseries?timestep=5m&id=${itemId}`);
      const data = await res.json();
      setTimeseries(data.data || []);
    } catch (e) {
      setTimeseries(null);
    } finally {
      setTsLoading(false);
    }
  };

  const enrichedItems = useMemo(() => {
    const itemMap = {};
    items.forEach((item) => (itemMap[item.id] = item));

    return Object.entries(prices)
      .map(([id, price]) => {
        const item = itemMap[parseInt(id)];
        if (!item || !price.high || !price.low) return null;

        const avg = avgPrices[id] || {};
        const buyPrice = price.low;   // instant-sell price = what patient buyers offer (bid)
        const sellPrice = price.high;  // instant-buy price = what patient sellers list at (ask)
        const fee = geFee(sellPrice);  // GE fee is on the sell side
        const margin = sellPrice - buyPrice - fee;  // profit = sell high - buy low - fee
        const roi = sellPrice > 0 ? (margin / buyPrice) * 100 : 0;
        const buyVol = avg.highPriceVolume || 0;
        const sellVol = avg.lowPriceVolume || 0;
        const volume = buyVol + sellVol;

        return {
          id: parseInt(id),
          name: item.name,
          icon: item.icon,
          members: item.members,
          limit: item.limit || 0,
          high: buyPrice,
          low: sellPrice,
          highTime: price.highTime,
          lowTime: price.lowTime,
          fee,
          margin,
          roi,
          volume,
          buyVol,
          sellVol,
          marginXVolume: margin * Math.min(volume, item.limit || volume),
          highalch: item.highalch || 0,
          alchProfit: item.highalch ? item.highalch - sellPrice - 150 : 0, // nature rune ~150gp
        };
      })
      .filter(Boolean);
  }, [items, prices, avgPrices]);

  const filtered = useMemo(() => {
    const preset = FILTER_PRESETS[filterPreset];
    return enrichedItems.filter((item) => {
      if (item.low < preset.minPrice || item.low > preset.maxPrice) return false;
      if (item.volume < preset.minVolume) return false;
      if (membersOnly && !item.members) return false;
      if (search && !item.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (item.margin <= 0) return false;
      return true;
    });
  }, [enrichedItems, filterPreset, membersOnly, search]);

  const sorted = useMemo(() => {
    const pinnedItems = filtered.filter((i) => pinned.has(i.id));
    const unpinnedItems = filtered.filter((i) => !pinned.has(i.id));
    
    const compare = (a, b) => {
      const dir = sortDir === "desc" ? -1 : 1;
      return (a[sortBy] - b[sortBy]) * dir;
    };
    
    pinnedItems.sort(compare);
    unpinnedItems.sort(compare);
    return [...pinnedItems, ...unpinnedItems];
  }, [filtered, sortBy, sortDir, pinned]);

  const displayItems = sorted.slice(0, 100);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const togglePin = (id) => {
    setPinned((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Sparkline mini chart for timeseries
  const Sparkline = ({ data, width = 280, height = 80 }) => {
    if (!data || data.length === 0) return null;
    const highs = data.filter((d) => d.avgHighPrice).map((d) => d.avgHighPrice);
    const lows = data.filter((d) => d.avgLowPrice).map((d) => d.avgLowPrice);
    const allPrices = [...highs, ...lows];
    const min = Math.min(...allPrices);
    const max = Math.max(...allPrices);
    const range = max - min || 1;

    const toPath = (points, key) => {
      const filtered = data.filter((d) => d[key]);
      if (filtered.length < 2) return "";
      return filtered
        .map((d, i) => {
          const x = (i / (filtered.length - 1)) * width;
          const y = height - ((d[key] - min) / range) * (height - 10) - 5;
          return `${i === 0 ? "M" : "L"} ${x} ${y}`;
        })
        .join(" ");
    };

    return (
      <svg width={width} height={height} style={{ display: "block" }}>
        <path d={toPath(data, "avgHighPrice")} fill="none" stroke="#f59e0b" strokeWidth="2" />
        <path d={toPath(data, "avgLowPrice")} fill="none" stroke="#60a5fa" strokeWidth="2" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <p style={styles.loadingText}>Loading GE prices...</p>
        <p style={styles.loadingSubtext}>Fetching data from the OSRS Wiki API</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.loadingContainer}>
        <p style={{ ...styles.loadingText, color: "#ef4444" }}>{error}</p>
        <button onClick={() => fetchData()} style={styles.retryBtn}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div>
            <h1 style={styles.title}>
              <span style={styles.titleIcon}>⚔️</span> GE Flip Tracker
            </h1>
            <p style={styles.subtitle}>
              Real-time OSRS Grand Exchange margins · {enrichedItems.length.toLocaleString()} items tracked
            </p>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.refreshInfo}>
              {lastRefresh && (
                <span style={styles.lastRefresh}>
                  Updated {formatTime(Math.floor(lastRefresh.getTime() / 1000))}
                </span>
              )}
              <button
                onClick={() => fetchData(true)}
                disabled={refreshing}
                style={{
                  ...styles.refreshBtn,
                  opacity: refreshing ? 0.5 : 1,
                }}
              >
                {refreshing ? "⟳" : "↻"} Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div style={styles.controls}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput}
            />
            {search && (
              <button onClick={() => setSearch("")} style={styles.clearSearch}>
                ✕
              </button>
            )}
          </div>

          <div style={styles.filterRow}>
            {FILTER_PRESETS.map((preset, i) => (
              <button
                key={preset.label}
                onClick={() => setFilterPreset(i)}
                style={{
                  ...styles.filterBtn,
                  ...(filterPreset === i ? styles.filterBtnActive : {}),
                }}
              >
                {preset.label}
              </button>
            ))}
            <button
              onClick={() => setMembersOnly(!membersOnly)}
              style={{
                ...styles.filterBtn,
                ...(membersOnly ? styles.filterBtnMembers : {}),
              }}
            >
              ⭐ Members
            </button>
          </div>

          <div style={styles.statsRow}>
            <span style={styles.stat}>
              Showing <strong>{displayItems.length}</strong> of {filtered.length} profitable items
            </span>
            <span style={styles.stat}>
              {pinned.size > 0 && `📌 ${pinned.size} pinned`}
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>📌</th>
              <th style={{ ...styles.th, textAlign: "left", minWidth: 180 }}>Item</th>
              <th
                style={{ ...styles.th, ...styles.thSortable }}
                onClick={() => toggleSort("low")}
              >
                Buy At{" "}
                {sortBy === "low" && (sortDir === "desc" ? "▼" : "▲")}
              </th>
              <th
                style={{ ...styles.th, ...styles.thSortable }}
                onClick={() => toggleSort("high")}
              >
                Sell At{" "}
                {sortBy === "high" && (sortDir === "desc" ? "▼" : "▲")}
              </th>
              <th
                style={{ ...styles.th, ...styles.thSortable }}
                onClick={() => toggleSort("margin")}
              >
                Margin{" "}
                {sortBy === "margin" && (sortDir === "desc" ? "▼" : "▲")}
              </th>
              <th
                style={{ ...styles.th, ...styles.thSortable }}
                onClick={() => toggleSort("roi")}
              >
                ROI %{" "}
                {sortBy === "roi" && (sortDir === "desc" ? "▼" : "▲")}
              </th>
              <th style={styles.th}>Limit</th>
              <th
                style={{ ...styles.th, ...styles.thSortable }}
                onClick={() => toggleSort("volume")}
              >
                Vol (5m){" "}
                {sortBy === "volume" && (sortDir === "desc" ? "▼" : "▲")}
              </th>
              <th
                style={{ ...styles.th, ...styles.thSortable }}
                onClick={() => toggleSort("marginXVolume")}
              >
                Margin×Vol{" "}
                {sortBy === "marginXVolume" && (sortDir === "desc" ? "▼" : "▲")}
              </th>
              <th style={styles.th}>Last Trade</th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((item) => {
              const isPinned = pinned.has(item.id);
              const isSelected = selectedItem?.id === item.id;
              return (
                <>
                  <tr
                    key={item.id}
                    style={{
                      ...styles.tr,
                      ...(isPinned ? styles.trPinned : {}),
                      ...(isSelected ? styles.trSelected : {}),
                    }}
                    onClick={() => {
                      if (selectedItem?.id === item.id) {
                        setSelectedItem(null);
                        setTimeseries(null);
                      } else {
                        setSelectedItem(item);
                        fetchTimeseries(item.id);
                      }
                    }}
                  >
                    <td style={styles.td}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePin(item.id);
                        }}
                        style={styles.pinBtn}
                      >
                        {isPinned ? "📌" : "○"}
                      </button>
                    </td>
                    <td style={{ ...styles.td, textAlign: "left" }}>
                      <div style={styles.itemName}>
                        <span style={styles.itemNameText}>{item.name}</span>
                        {item.members && <span style={styles.membersBadge}>P2P</span>}
                      </div>
                    </td>
                    <td style={{ ...styles.td, color: "#60a5fa", fontWeight: 600 }}>
                      {formatGp(item.low)}
                    </td>
                    <td style={{ ...styles.td, color: "#f59e0b", fontWeight: 600 }}>
                      {formatGp(item.high)}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        color: item.margin > 0 ? "#22c55e" : "#ef4444",
                        fontWeight: 700,
                      }}
                    >
                      {formatGp(item.margin)}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.roiBadge,
                          background: roiColor(item.roi) + "22",
                          color: roiColor(item.roi),
                          borderColor: roiColor(item.roi) + "44",
                        }}
                      >
                        {item.roi.toFixed(1)}%
                      </span>
                    </td>
                    <td style={{ ...styles.td, color: "#94a3b8" }}>
                      {item.limit ? item.limit.toLocaleString() : "?"}
                    </td>
                    <td style={{ ...styles.td, color: "#94a3b8" }}>
                      {item.volume.toLocaleString()}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        color: "#d4a017",
                        fontWeight: 600,
                      }}
                    >
                      {formatGp(item.marginXVolume)}
                    </td>
                    <td style={{ ...styles.td, color: "#64748b", fontSize: 12 }}>
                      {formatTime(Math.max(item.highTime || 0, item.lowTime || 0))}
                    </td>
                  </tr>
                  {isSelected && (
                    <tr key={`${item.id}-detail`}>
                      <td colSpan={10} style={styles.detailCell}>
                        <div style={styles.detailPanel}>
                          <div style={styles.detailGrid}>
                            <div style={styles.detailCard}>
                              <div style={styles.detailLabel}>Flip Calculator</div>
                              <div style={styles.detailRow}>
                                <span>Buy {item.limit || "?"} × {formatGp(item.low)}</span>
                                <span style={{ color: "#60a5fa" }}>
                                  = {formatGp(item.low * (item.limit || 1))}
                                </span>
                              </div>
                              <div style={styles.detailRow}>
                                <span>Sell {item.limit || "?"} × {formatGp(item.high)}</span>
                                <span style={{ color: "#f59e0b" }}>
                                  = {formatGp(item.high * (item.limit || 1))}
                                </span>
                              </div>
                              <div style={styles.detailRow}>
                                <span>GE Fee per item</span>
                                <span style={{ color: "#ef4444" }}>-{formatGp(item.fee)}</span>
                              </div>
                              <div style={{ ...styles.detailRow, borderTop: "1px solid #334155", paddingTop: 8, marginTop: 4 }}>
                                <span style={{ fontWeight: 700 }}>Profit per limit</span>
                                <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 16 }}>
                                  {formatGp(item.margin * (item.limit || 1))}
                                </span>
                              </div>
                            </div>
                            <div style={styles.detailCard}>
                              <div style={styles.detailLabel}>Price History (5m intervals)</div>
                              {tsLoading ? (
                                <p style={{ color: "#64748b", fontSize: 13 }}>Loading chart...</p>
                              ) : timeseries ? (
                                <div>
                                  <Sparkline data={timeseries.slice(-60)} width={280} height={80} />
                                  <div style={styles.chartLegend}>
                                    <span style={{ color: "#f59e0b" }}>● Buy price</span>
                                    <span style={{ color: "#60a5fa" }}>● Sell price</span>
                                  </div>
                                </div>
                              ) : (
                                <p style={{ color: "#64748b", fontSize: 13 }}>No data available</p>
                              )}
                            </div>
                            <div style={styles.detailCard}>
                              <div style={styles.detailLabel}>Quick Stats</div>
                              <div style={styles.detailRow}>
                                <span>High Alch</span>
                                <span>{formatGp(item.highalch)}</span>
                              </div>
                              <div style={styles.detailRow}>
                                <span>Alch Profit</span>
                                <span style={{ color: item.alchProfit > 0 ? "#22c55e" : "#ef4444" }}>
                                  {formatGp(item.alchProfit)}
                                </span>
                              </div>
                              <div style={styles.detailRow}>
                                <span>Buy Vol (5m)</span>
                                <span>{item.buyVol}</span>
                              </div>
                              <div style={styles.detailRow}>
                                <span>Sell Vol (5m)</span>
                                <span>{item.sellVol}</span>
                              </div>
                              <div style={styles.detailRow}>
                                <span>
                                  <a
                                    href={`https://prices.runescape.wiki/osrs/item/${item.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.wikiLink}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    📊 Wiki Price Page →
                                  </a>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p>
          Data from <a href="https://prices.runescape.wiki/osrs/" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>OSRS Wiki Real-time Prices API</a> (RuneLite partnership). 
          Prices refresh every 2 minutes. Margin = Buy − Sell − 1% GE fee. ROI = Margin ÷ Sell price.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    background: "linear-gradient(180deg, #0c0f1a 0%, #111827 50%, #0f172a 100%)",
    color: "#e2e8f0",
    minHeight: "100vh",
    padding: 0,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#0c0f1a",
    fontFamily: "'JetBrains Mono', monospace",
  },
  loadingSpinner: {
    width: 48,
    height: 48,
    border: "3px solid #1e293b",
    borderTop: "3px solid #f59e0b",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: 16,
  },
  loadingText: { color: "#e2e8f0", fontSize: 16, margin: 0 },
  loadingSubtext: { color: "#64748b", fontSize: 13, marginTop: 4 },
  retryBtn: {
    marginTop: 16,
    padding: "8px 20px",
    background: "#f59e0b",
    color: "#0f172a",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 700,
    fontFamily: "inherit",
  },
  header: {
    padding: "24px 24px 0",
    borderBottom: "1px solid #1e293b",
    position: "sticky",
    top: 0,
    background: "#0c0f1aee",
    backdropFilter: "blur(12px)",
    zIndex: 100,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 800,
    color: "#f59e0b",
    letterSpacing: "-0.5px",
  },
  titleIcon: { marginRight: 8 },
  subtitle: { margin: "4px 0 0", fontSize: 13, color: "#64748b" },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  refreshInfo: { display: "flex", alignItems: "center", gap: 8 },
  lastRefresh: { fontSize: 12, color: "#475569" },
  refreshBtn: {
    padding: "6px 14px",
    background: "#1e293b",
    color: "#f59e0b",
    border: "1px solid #334155",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 600,
  },
  controls: { padding: "16px 0" },
  searchWrap: {
    position: "relative",
    marginBottom: 12,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 14,
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "10px 36px 10px 36px",
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "#e2e8f0",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  },
  clearSearch: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    fontSize: 14,
    padding: 4,
  },
  filterRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  filterBtn: {
    padding: "6px 14px",
    background: "#1e293b",
    color: "#94a3b8",
    border: "1px solid #334155",
    borderRadius: 20,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "inherit",
    transition: "all 0.15s",
  },
  filterBtnActive: {
    background: "#f59e0b22",
    color: "#f59e0b",
    borderColor: "#f59e0b55",
  },
  filterBtnMembers: {
    background: "#22c55e22",
    color: "#22c55e",
    borderColor: "#22c55e55",
  },
  statsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 8,
  },
  stat: { fontSize: 12, color: "#64748b" },
  tableWrap: {
    overflowX: "auto",
    padding: "0 24px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    padding: "10px 12px",
    textAlign: "right",
    color: "#64748b",
    fontWeight: 700,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #1e293b",
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  thSortable: { cursor: "pointer" },
  tr: {
    borderBottom: "1px solid #1e293b22",
    cursor: "pointer",
    transition: "background 0.1s",
  },
  trPinned: {
    background: "#f59e0b08",
    borderLeft: "2px solid #f59e0b",
  },
  trSelected: {
    background: "#1e293b",
  },
  td: {
    padding: "10px 12px",
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  pinBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    padding: 2,
  },
  itemName: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  itemNameText: {
    fontWeight: 600,
    color: "#e2e8f0",
  },
  membersBadge: {
    fontSize: 9,
    padding: "1px 5px",
    background: "#f59e0b22",
    color: "#f59e0b",
    borderRadius: 4,
    fontWeight: 700,
  },
  roiBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 700,
    border: "1px solid",
  },
  detailCell: {
    padding: 0,
    background: "#0f172a",
    borderBottom: "2px solid #f59e0b33",
  },
  detailPanel: {
    padding: "16px 20px",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 16,
  },
  detailCard: {
    background: "#1e293b",
    borderRadius: 8,
    padding: 16,
    border: "1px solid #334155",
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 10,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13,
    padding: "4px 0",
    color: "#94a3b8",
  },
  chartLegend: {
    display: "flex",
    gap: 16,
    fontSize: 11,
    marginTop: 6,
  },
  wikiLink: {
    color: "#60a5fa",
    textDecoration: "none",
    fontSize: 13,
  },
  footer: {
    padding: "20px 24px",
    textAlign: "center",
    fontSize: 11,
    color: "#475569",
    borderTop: "1px solid #1e293b",
    marginTop: 20,
  },
  footerLink: {
    color: "#60a5fa",
    textDecoration: "none",
  },
};

// inject keyframes
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700;800&display=swap');
    tr:hover { background: #1e293b88 !important; }
    input:focus { border-color: #f59e0b !important; }
    button:hover { opacity: 0.85; }
    ::-webkit-scrollbar { width: 8px; height: 8px; }
    ::-webkit-scrollbar-track { background: #0f172a; }
    ::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }
  `;
  document.head.appendChild(style);
}
